"use client";

import { usePathname } from "next/navigation";
import { useEffect } from "react";

import { track } from "./data-layer";
import { loadAnalyticsVisitor, buildAnalyticsUser } from "./analytics-user";
import { readAnalyticsIdentity } from "./identity-cookies-client";
import { getAnalyticsList, whenAnalyticsList, type AnalyticsList } from "./list-context";
import { resolvePage } from "./page-view";

type Visitor = Awaited<ReturnType<typeof loadAnalyticsVisitor>>;

/**
 * The `page_view` of a list route that is still waiting for its page to say which list it shows.
 * Kept at module level so that the **next** navigation can send it, without the list, if the person
 * moved on before the page arrived (a slow read of the list): a `page_view` must not be lost to that.
 */
let pending: { pathname: string; flush: () => void } | null = null;

/**
 * The site's one `page_view`: one per navigation, on every route (INST §08, "toda carga e toda
 * troca de rota no cliente"), with `page_type`, `list_id` and `viewer_role`, and the `user` and
 * `list` blocks of §10.
 *
 * Mounted once in the root layout. It replaces the host panel's own, so there are never two.
 *
 * **Never without the list, never twice.** A list route waits for its page to publish the list
 * (`ListContextPublisher`) and sends once, with it, **however long the page takes** (R-4: a fixed
 * wait sent the `page_view` without the list when the read of the list was slow). If the person
 * leaves first (the next navigation, or `pagehide`), the waiting `page_view` is sent then, without
 * the list, so it is never lost. Leaving a list route clears the list, so the next route cannot
 * inherit it. The effect is asynchronous and cancels itself on cleanup, which is also what makes
 * React's development-only double effect produce one event, not two.
 *
 * The panel's tabs keep their query (`?lente=…`, the shareable slice); no other route does, because
 * a query there can hold what a person typed.
 */
export function PageViewTracker() {
  const pathname = usePathname();

  useEffect(() => {
    let cancelled = false;
    let done = false;
    let visitor: Visitor | null = null;
    const page = resolvePage(pathname);
    const search = page.pageType === "host_panel" ? window.location.search : "";
    const visitorPromise = loadAnalyticsVisitor().then((loaded) => {
      visitor = loaded;

      return loaded;
    });

    function send(loaded: Visitor, list: AnalyticsList | null): void {
      if (done) return;
      done = true;
      if (pending === entry) pending = null;

      // The panel is the host's own list, which no page publishes: the session's pointer names it.
      const ownList: AnalyticsList | null =
        page.pageType === "host_panel" && loaded.kind === "customer" && loaded.giftListId !== null
          ? { list_id: loaded.giftListId }
          : null;
      const pageList = page.pageType === "host_panel" ? ownList : list;

      const user = buildAnalyticsUser(loaded, { pathname, listId: pageList?.list_id ?? null }, readAnalyticsIdentity());

      track({
        event: "page_view",
        page_type: page.pageType,
        page_path: `${page.pagePath}${search}`,
        ...(page.hostPanelTab === undefined ? {} : { host_panel_tab: page.hostPanelTab }),
        viewer_role: user.viewer_role,
        ...(pageList === null ? {} : { list_id: pageList.list_id, list: pageList }),
        user,
      });
    }

    /** Sends it now, without the list: the person is leaving before the page said which one it shows. */
    function flush(): void {
      if (done) return;
      if (visitor !== null) send(visitor, null);
      else void visitorPromise.then((loaded) => send(loaded, null));
    }

    const entry = page.expectsList ? { pathname, flush } : null;

    // The previous route's `page_view` was still waiting: it goes out now. The same path again is
    // React's development double effect, whose first run is replaced, not flushed.
    if (pending !== null && pending.pathname !== pathname) pending.flush();
    pending = entry;

    void (async () => {
      const [loaded, list] = await Promise.all([
        visitorPromise,
        page.expectsList ? whenAnalyticsList() : Promise.resolve(getAnalyticsList()),
      ]);
      if (cancelled) return;

      send(loaded, list);
    })();

    const onPageHide = () => {
      if (entry !== null) flush();
    };
    window.addEventListener("pagehide", onPageHide);

    return () => {
      cancelled = true;
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [pathname]);

  return null;
}
